/**
 * Welcome Content Service
 *
 * Fetches editable landing content from the haat-hub portal-backend public
 * endpoint. This is a SEPARATE backend from the Courier Connect backend that
 * `apiClient` targets, so it uses its own base URL
 * (`REACT_APP_PORTAL_API_BASE_URL`).
 *
 * The endpoint is public (no auth/cookies). The portal-backend wraps responses
 * as `{ success, data, ... }`, so we unwrap `.data.data`.
 */

import axios from 'axios';
import { resolveLanUrl } from 'shared/lib/resolveLanUrl';
import { getRuntimeEnv } from 'shared/lib/runtimeConfig';
import type { WelcomeContent } from 'features/courier-onboarding/components/welcome/welcomeContent';

const PORTAL_BASE_URL = resolveLanUrl(
  getRuntimeEnv('REACT_APP_PORTAL_API_BASE_URL') || 'http://localhost:3117',
);

const PUBLIC_ENDPOINT = '/api/v1/courier-connect/public/landing-content';

const portalClient = axios.create({
  baseURL: PORTAL_BASE_URL,
  withCredentials: false,
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

type WrappedResponse =
  | { data?: WelcomeContent }
  | WelcomeContent;

const unwrap = (payload: WrappedResponse): WelcomeContent => {
  if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
    return (payload as { data: WelcomeContent }).data;
  }
  return payload as WelcomeContent;
};

/**
 * Fetch published landing content. Throws on network/HTTP errors so callers
 * can fall back to the bundled i18n copy.
 */
export const fetchWelcomeContent = async (): Promise<WelcomeContent> => {
  const { data } = await portalClient.get<WrappedResponse>(PUBLIC_ENDPOINT);
  return unwrap(data);
};
